/**
 * Compact cart quick-add card. Lets a customer pick a variant and add it
 * without leaving the cart. No client-side money formatting — every price
 * shown is copied from a value Liquid already formatted with
 * Shopify's active currency settings.
 */
(() => {
  // Threshold for the free-shipping upsell headline below. Kept in sync
  // with the cart page's own shipping-progress logic
  // (sections/main-cart-footer.liquid).
  const FREE_SHIPPING_THRESHOLD_CENTS = 2500;
  // Paused while shipping is free on all contiguous-US orders — there's no
  // threshold left to "unlock". Set true to restore the upsell headline.
  const FREE_SHIPPING_UPSELL_ENABLED = false;

  function initQuickAddCard(card) {
    // The cart page fully replaces this card's markup (new DOM nodes) any
    // time a quantity changes elsewhere in the cart — see the re-scan hook
    // at the bottom of this file. Guard against double-attaching listeners
    // if init ever runs twice for the same still-alive node.
    if (card.dataset.quickAddInitialized === 'true') return;
    card.dataset.quickAddInitialized = 'true';

    const productId = card.id.replace('QuickAdd-', '');
    const headlineEl = card.querySelector('[data-quick-add-headline]');
    const imageEl = document.getElementById(`QuickAddImage-${productId}`);
    const imageButton = document.getElementById(`QuickAddImageButton-${productId}`);
    const lightbox = document.getElementById(`QuickAddLightbox-${productId}`);
    const lightboxImage = document.getElementById(`QuickAddLightboxImage-${productId}`);
    const priceEl = document.getElementById(`QuickAddPrice-${productId}`);
    const addButton = document.getElementById(`QuickAddButton-${productId}`);
    const addButtonText = addButton ? addButton.querySelector('.ift-quick-add__add-text') : null;
    const spinner = addButton ? addButton.querySelector('.loading__spinner') : null;
    const swatches = Array.from(card.querySelectorAll('.ift-quick-add__swatch'));
    const cartTotalCents = parseInt(card.dataset.cartTotalCents, 10) || 0;
    const qtyInput = document.getElementById(`QuickAddQty-${productId}`);
    const qtyDecrease = card.querySelector('[data-qty-decrease]');
    const qtyIncrease = card.querySelector('[data-qty-increase]');

    if (!addButton) return;

    function clampQty(value, min, max, step) {
      let n = parseInt(value, 10);
      if (isNaN(n)) n = min;
      if (n < min) n = min;
      if (max != null && n > max) n = max;
      if (step > 1) {
        const offset = (n - min) % step;
        if (offset !== 0) n += step - offset;
        if (max != null && n > max) n = max;
      }
      return n;
    }

    function getQtyBounds() {
      if (!qtyInput) return { min: 1, max: null, step: 1 };
      const min = parseInt(qtyInput.min, 10) || 1;
      const max = qtyInput.max ? parseInt(qtyInput.max, 10) : null;
      const step = parseInt(qtyInput.step, 10) || 1;
      return { min, max, step };
    }

    function getQty() {
      if (!qtyInput) return 1;
      const { min, max, step } = getQtyBounds();
      return clampQty(qtyInput.value, min, max, step);
    }

    function updateHeadline() {
      if (!headlineEl) return;
      const variantPriceCents = parseInt(addButton.dataset.selectedVariantPrice, 10) || 0;
      const wouldBeTotal = cartTotalCents + variantPriceCents * getQty();
      const crossesThreshold =
        FREE_SHIPPING_UPSELL_ENABLED &&
        cartTotalCents < FREE_SHIPPING_THRESHOLD_CENTS && wouldBeTotal >= FREE_SHIPPING_THRESHOLD_CENTS;
      headlineEl.textContent = crossesThreshold
        ? 'Add this and unlock free shipping'
        : 'Complete your protection setup';
    }

    if (qtyInput) {
      function commitQty() {
        const { min, max, step } = getQtyBounds();
        qtyInput.value = clampQty(qtyInput.value, min, max, step);
        updateHeadline();
      }
      qtyInput.addEventListener('change', commitQty);
      qtyInput.addEventListener('blur', commitQty);
      if (qtyDecrease) {
        qtyDecrease.addEventListener('click', () => {
          const { min, max, step } = getQtyBounds();
          qtyInput.value = clampQty(getQty() - step, min, max, step);
          updateHeadline();
        });
      }
      if (qtyIncrease) {
        qtyIncrease.addEventListener('click', () => {
          const { min, max, step } = getQtyBounds();
          qtyInput.value = clampQty(getQty() + step, min, max, step);
          updateHeadline();
        });
      }
    }

    function selectSwatch(swatch) {
      if (!swatch || swatch.disabled) return;
      swatches.forEach((s) => {
        s.classList.remove('is-selected');
        s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('is-selected');
      swatch.setAttribute('aria-pressed', 'true');

      const variantId = swatch.dataset.variantId;
      const priceCents = parseInt(swatch.dataset.variantPrice, 10) || 0;
      const priceFormatted = swatch.dataset.variantPriceFormatted;
      const image = swatch.dataset.variantImage;
      const zoomImage = swatch.dataset.variantZoomImage;
      const title = swatch.dataset.variantTitle;

      addButton.dataset.selectedVariantId = variantId;
      addButton.dataset.selectedVariantPrice = priceCents;
      if (priceEl && priceFormatted) priceEl.textContent = priceFormatted;
      if (imageEl && image) {
        imageEl.src = image;
        imageEl.alt = title || '';
        if (zoomImage) imageEl.dataset.zoomSrc = zoomImage;
      }
      if (qtyInput) {
        // Different colors could in principle carry different quantity
        // rules (case packs, min-order colors, etc.) — re-read them from
        // the newly selected swatch and re-clamp the current quantity so
        // it can't be left outside the new variant's allowed range.
        const min = parseInt(swatch.dataset.variantQtyMin, 10) || 1;
        const max = swatch.dataset.variantQtyMax ? parseInt(swatch.dataset.variantQtyMax, 10) : null;
        const step = parseInt(swatch.dataset.variantQtyStep, 10) || 1;
        qtyInput.min = min;
        if (max != null) {
          qtyInput.max = max;
        } else {
          qtyInput.removeAttribute('max');
        }
        qtyInput.step = step;
        qtyInput.value = clampQty(qtyInput.value, min, max, step);
      }
      updateHeadline();
    }

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => selectSwatch(swatch));
    });

    function openLightbox() {
      if (!lightbox || !lightboxImage || !imageEl) return;
      const zoomSrc = imageEl.dataset.zoomSrc || imageEl.src;
      lightboxImage.src = zoomSrc;
      lightboxImage.alt = imageEl.alt || '';
      lightbox.hidden = false;
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      if (lightboxImage) lightboxImage.src = '';
    }

    if (imageButton) {
      imageButton.addEventListener('click', openLightbox);
    }

    if (lightbox) {
      lightbox.addEventListener('click', (event) => {
        // Close on backdrop click or the explicit close button, but not
        // when the click lands on the enlarged image itself.
        if (event.target === lightbox || event.target.closest('[data-lightbox-close]')) {
          closeLightbox();
        }
      });
    }

    function setSubmitting(isSubmitting) {
      if (isSubmitting) {
        addButton.setAttribute('aria-disabled', 'true');
      } else {
        addButton.removeAttribute('aria-disabled');
      }
      if (spinner) spinner.classList.toggle('hidden', !isSubmitting);
      if (addButtonText) addButtonText.classList.toggle('hidden', isSubmitting);
    }

    let submitting = false;
    addButton.addEventListener('click', () => {
      if (submitting || addButton.getAttribute('aria-disabled') === 'true') return;
      const variantId = addButton.dataset.selectedVariantId;
      if (!variantId) return;

      submitting = true;
      setSubmitting(true);

      fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: getQty() }] }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            // Added item failed (e.g. went out of stock between page load
            // and click) — re-enable so the customer can retry or pick a
            // different color instead of getting stuck.
            submitting = false;
            setSubmitting(false);
            return;
          }
          // Reload so the cart (line items, totals, shipping progress, and
          // this card's own recommendation) all reflect the new state
          // together, rather than hand-patching several independent
          // sections in place.
          window.location.reload();
        })
        .catch(() => {
          submitting = false;
          setSubmitting(false);
        });
    });
  }

  function init() {
    document.querySelectorAll('.ift-quick-add').forEach(initQuickAddCard);
  }

  // Registered once for the page's lifetime (not inside initQuickAddCard,
  // which reruns every time the cart replaces this card's markup) so it
  // never accumulates duplicate listeners. Delegates to whichever lightbox
  // is actually open at the time, so it stays correct across re-renders.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openLightbox = document.querySelector('.ift-quick-add__lightbox:not([hidden])');
    if (openLightbox) openLightbox.hidden = true;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The cart page (main-cart-footer) replaces this card's entire markup
  // with freshly-fetched HTML on every quantity change elsewhere in the
  // cart, which destroys these event listeners along with the old nodes.
  // Re-scan and re-attach after every such update.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => init());
    subscribe(PUB_SUB_EVENTS.quantityUpdate, () => init());
  }
})();
